// src/pages/equipmentrentaladmin/maintenance/Maintenance.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import { FaTools } from "react-icons/fa";
import { MdBuild, MdWarning, MdCheckCircle } from 'react-icons/md';
import { FiEye, FiAlertCircle, FiStar, FiTrash2, FiMoreVertical, FiX } from 'react-icons/fi';
import { Row, Col, Form } from 'react-bootstrap';
import RentalAdminLayout from '../../../layout/equipmentrentaladmin/RentalAdminLayout';
import AppPagination from '../../../components/general/pagination/Pagination.js';
import '../../../assets/css/equipmentrentaladmin/maintenance/Maintenance.css';
import axios from "axios";
import Swal from 'sweetalert2';
import AppContext from "../../../context/AppContext";

const Maintenance = () => {
 
  const navigate = useNavigate();
  const { baseUrl, loggedInRental, baseUrlForFile , authToken} = useContext(AppContext);

  // ── States ──
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeMenu, setActiveMenu] = useState(null);

  // Dispute states
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');

  // Review states
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(0);

  // ── FETCH ────────────────────────────────────────────────────────
  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const viewRentalMaintenanceRequests = () => {
    if (!loggedInRental) return;
    axios.post(`${baseUrl}/rentaladmin/maintenance/viewRentalMaintenanceRequests`, {
      srrentaladmin_id: loggedInRental.id
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      console.log("API Response:", res.data);
      if (res.data.success) setMaintenanceRecords(res.data.requests);
    }).catch((err) => { console.log("API Error:", err); });
  };

  useEffect(() => {
    if (loggedInRental?.id) viewRentalMaintenanceRequests();
  }, [loggedInRental]);

  // ── PAGINATION ───────────────────────────────────────────────────
  const recordsPerPage = 5;
  const displayRecords = Array.isArray(maintenanceRecords) ? maintenanceRecords : [];
  const totalPages = Math.ceil(displayRecords.length / recordsPerPage) || 1;
  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = displayRecords.slice(indexOfFirst, indexOfLast);

  // ── STAT COUNTS ──────────────────────────────────────────────────
  const pendingCount = displayRecords.filter(r => r.status === "pending").length;
  const inProgressCount = displayRecords.filter(r => r.status === "in-progress").length;
  const completedCount = displayRecords.filter(r => r.status === "completed").length;

  // Close dropdown on outside click
  useEffect(() => {
    const close = (e) => { if (!e.target.closest('.mnt-actions-wrapper')) setActiveMenu(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // ── STATUS HELPER ────────────────────────────────────────────────
  const getStatusInfo = (status) => {
    const map = {
      'pending': { color: '#FDB714', bg: '#FDB71420', text: 'Pending' },
      'scheduled': { color: '#007bff', bg: '#007bff20', text: 'Scheduled' },
      'in-progress': { color: '#FDB714', bg: '#FDB71420', text: 'In Progress' },
      'completed': { color: '#28a745', bg: '#28a74520', text: 'Completed' },
      'cancelled': { color: '#e8590c', bg: '#e8590c20', text: 'Cancelled' },
    };
    return map[status] || { color: '#666', bg: '#66666620', text: status };
  };

  // ── HANDLERS ─────────────────────────────────────────────────────
  const handleMaintenanceRequest = () => navigate("/rental-admin/requestbutton");

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
    setActiveMenu(null);
  };

  const handleReview = (record) => {
    setSelectedRecord(record);
    setReviewText('');
    setRating(0);
    setShowReviewModal(true);
    setActiveMenu(null);
  };

  const handleDispute = (record) => {
    setSelectedRecord(record);
    setCategory('');
    setDescription('');
    setShowDisputeModal(true);
    setActiveMenu(null);
  };

  const handleDelete = (record) => {
    setActiveMenu(null);
    Swal.fire({
      icon: 'warning', title: 'Are you sure?',
      text: 'This record will be deleted.',
      showCancelButton: true,
      confirmButtonColor: '#FDB714',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, delete',
    }).then((result) => {
      if (result.isConfirmed) {
        setMaintenanceRecords(prev => prev.filter(r => r.id !== record.id));
      }
    });
  };

  // ── CANCEL BOOKING — koi advance payment involved nahi, seedha status cancel hota hai ──
  const handleCancelBooking = (record) => {
    setActiveMenu(null);
    Swal.fire({
      icon: 'warning',
      title: 'Cancel This Booking?',
      text: 'Are you sure you want to cancel this mechanic booking? This action cannot be undone.',
      showCancelButton: true,
      confirmButtonColor: '#e8590c',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, Cancel Booking',
      cancelButtonText: 'No, Keep Booking',
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios
        .post(
          `${baseUrl}/rentaladmin/maintenance/cancelMechanicBooking`,
          {
            booking_id: record.id,
            srrentaladmin_id: loggedInRental.id,
          },
          { headers: { Authorization: `Bearer ${authToken}` } },
        )
        .then((res) => {
          if (!res.data.success) {
            Swal.fire({ icon: 'error', title: 'Could not cancel', text: res.data.message || 'Please try again.' });
            return;
          }
          Swal.fire({ icon: 'success', title: 'Booking Cancelled', confirmButtonColor: '#FDB714' });
          viewRentalMaintenanceRequests();
        })
        .catch((err) => {
          console.log(err);
          Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong while cancelling the booking.' });
        });
    });
  };

  // ── REVIEW SUBMIT — API call ─────────────────────────────────────
  const handleReviewSubmit = (e) => {
    e.preventDefault();

    if (rating === 0) {
      Swal.fire({ icon: 'warning', title: 'Rating Required', text: 'Please select a rating.', confirmButtonColor: '#FDB714' });
      return;
    }

    const formData = new FormData();
    formData.append('id', selectedRecord.id);
    formData.append('rating', rating);
    formData.append('review', reviewText);

    axios.post(`${baseUrl}/rentaladmin/maintenance/submitMechanicReviewByRental`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' ,
        Authorization: `Bearer ${authToken}`
      },
    }).then((res) => {
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Review Submitted!',
          text: 'Thank you for your feedback.',
          confirmButtonColor: '#FDB714',
        });
        setReviewText('');
        setRating(0);
        setShowReviewModal(false);
      } else {
        Swal.fire({ icon: 'error', title: 'Oops!', text: res.data.message || 'Failed to submit review.', confirmButtonColor: '#FDB714' });
      }
    }).catch(() => {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong. Please try again.', confirmButtonColor: '#FDB714' });
    });
  };

  // ── DISPUTE SUBMIT ───────────────────────────────────────────────
  // ✅ FIX: Authorization moved from request body into the axios `headers` option.
  const riseDispute = (e) => {
    e.preventDefault();
    if (!category || !description) {
      Swal.fire({ icon: 'warning', title: 'Fields Required', text: 'Please fill all fields', confirmButtonColor: '#FDB714' });
      return;
    }

    const mechanicName =
      selectedRecord.srmechanicadmin?.fullname ||
      selectedRecord.fullname ||
      `Request #${selectedRecord.id}`;

    axios.post(`${baseUrl}/rentaladmin/dispute/riseDispute`, {
      srrentaladmin_id: loggedInRental.id,
      srmaintenancerequest_id: selectedRecord.id,
      srmechanicadmin_id: selectedRecord.srmechanicadmin_id || null,
      raisedby: loggedInRental?.businessname || loggedInRental?.fullname || `Rental #${loggedInRental?.id}`,
      raisedto: mechanicName,
      category: category,
      description: description
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Dispute Raised!', text: 'Your dispute has been submitted successfully.', confirmButtonColor: '#FDB714' });
        setCategory('');
        setDescription('');
        setShowDisputeModal(false);
      } else {
        Swal.fire({ icon: 'error', title: 'Failed', text: 'Failed to raise dispute. Please try again.', confirmButtonColor: '#FDB714' });
      }
    }).catch(() => {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong.', confirmButtonColor: '#FDB714' });
    });
  };

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <RentalAdminLayout>
      <div className="mnt-page">

        {/* PAGE HEADER */}
        <div className="mnt-page-header">
          <h2 className="mnt-page-title">Maintenance Management</h2>
          <button className="mnt-request-btn" onClick={handleMaintenanceRequest}>
            <FaTools size={14} /> Maintenance Request
          </button>
        </div>

        {/* STAT CARDS */}
        <Row className="mb-4">
          <Col md={4}>
            <div className="mnt-stat-card" style={{ borderLeft: '4px solid #FDB714' }}>
              <div className="mnt-stat-icon mnt-icon-yellow"><MdBuild /></div>
              <div className="mnt-stat-info"><h3>{pendingCount}</h3><p>Pending</p></div>
            </div>
          </Col>
          <Col md={4}>
            <div className="mnt-stat-card" style={{ borderLeft: '4px solid #FDB714' }}>
              <div className="mnt-stat-icon mnt-icon-yellow"><MdWarning /></div>
              <div className="mnt-stat-info"><h3>{inProgressCount}</h3><p>In Progress</p></div>
            </div>
          </Col>
          <Col md={4}>
            <div className="mnt-stat-card" style={{ borderLeft: '4px solid #FDB714' }}>
              <div className="mnt-stat-icon mnt-icon-yellow"><MdCheckCircle /></div>
              <div className="mnt-stat-info"><h3>{completedCount}</h3><p>Completed</p></div>
            </div>
          </Col>
        </Row>

        {/* TABLE */}
        <div className="mnt-table-section">
          <div className="mnt-table-header">
            <h3 className="mnt-table-title">My Maintenance Requests</h3>
          </div>

          <div className="mnt-table-wrapper">
            <table className="mnt-table">
              <thead>
                <tr>

                  <th>MECHANIC Admin</th>
                  <th>MACHINERY TYPE</th>
                  <th>PREFERRED DATE</th>
                  <th>BOOKING FEE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.length > 0 ? currentRecords.map((record) => {
                  const statusInfo = getStatusInfo(record.status);
                  return (
                    <tr key={record.id}>

                      <td>
                        <div className="mnt-seller-cell">
                          <img
                            src={`${baseUrlForFile}/${record.srmechanicadmin?.image}`}
                            alt={record.srmechanicadmin?.fullname}
                            className="mnt-seller-avatar"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="mnt-seller-info">
                            <div className="mnt-seller-name">
                              {record.srmechanicadmin?.fullname || "N/A"}
                            </div>
                            <div className="mnt-seller-email">
                              {record.srmechanicadmin?.email || ""}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{record.machinerytype}</td>
                      <td>{record.preferreddate}</td>
                      <td>{record.bookingfee}</td>
                      <td>
                        <span className="mnt-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>
                          {statusInfo.text}
                        </span>
                      </td>
                      <td className="mnt-actions-cell">
                        <div className="mnt-actions-wrapper">
                          <button className="mnt-menu-btn"
                            onClick={() => setActiveMenu(activeMenu === record.id ? null : record.id)}>
                            <FiMoreVertical size={20} />
                          </button>
                          {activeMenu === record.id && (
                            <div className="mnt-dropdown">
                              <button className="mnt-drop-item mnt-drop-view"
                                onClick={() => handleView(record)}>
                                <FiEye size={14} /> View
                              </button>
                              <button className="mnt-drop-item mnt-drop-dispute"
                                onClick={() => handleDispute(record)}>
                                <FiAlertCircle size={14} /> Dispute
                              </button>
                              {(record.status === 'pending' ||
                                record.status === 'accepted' ||
                                record.status === 'in_progress') && (
                                <button className="mnt-drop-item"
                                  style={{ color: '#e8590c' }}
                                  onClick={() => handleCancelBooking(record)}>
                                  <FiX size={14} /> Cancel Booking
                                </button>
                              )}
                              {record.status === 'completed' && (
                                <button className="mnt-drop-item mnt-drop-review"
                                  onClick={() => handleReview(record)}>
                                  <FiStar size={14} /> Write a Review
                                </button>
                              )}
                              <button className="mnt-drop-item mnt-drop-delete"
                                onClick={() => handleDelete(record)}>
                                <FiTrash2 size={14} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="7" className="text-center" style={{ padding: '40px', color: '#888' }}>
                      No Maintenance Requests Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <AppPagination
            currentPage={currentPage} totalPages={totalPages}
            totalItems={displayRecords.length} itemsPerPage={recordsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      {/* ════ VIEW MODAL ════ */}
      {showViewModal && selectedRecord && (
        <div className="mnt-overlay" onClick={() => setShowViewModal(false)}>
          <div className="mnt-modal" onClick={e => e.stopPropagation()}>
            <div className="mnt-modal-header">
              <div className="mnt-modal-header-left">
                <h5>Maintenance Detail</h5>
                <span>Full breakdown of this maintenance request</span>
              </div>
              <button className="mnt-modal-close" onClick={() => setShowViewModal(false)}><FiX /></button>
            </div>
            <div className="mnt-modal-body">
              <div className="mnt-modal-status-bar">
                <div className="mnt-sb-left">
                  <span className="mnt-sb-id">#{selectedRecord.id} — {selectedRecord.fullname}</span>
                  <span className="mnt-status-badge"
                    style={{ background: getStatusInfo(selectedRecord.status).bg, color: getStatusInfo(selectedRecord.status).color }}>
                    {getStatusInfo(selectedRecord.status).text}
                  </span>
                </div>
              </div>

              <p className="mnt-section-title">Mechanic Information</p>
              <div className="mnt-mechanic-strip">
                <img
                  src={`${baseUrlForFile}/${selectedRecord.srmechanicadmin?.image}`}
                  alt={selectedRecord.srmechanicadmin?.fullname}
                  className="mnt-mechanic-avatar-img"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="mnt-mechanic-info">
                  <div className="mnt-mechanic-name">
                    {selectedRecord.srmechanicadmin?.fullname || "N/A"}
                  </div>
                  <div className="mnt-mechanic-email">
                    {selectedRecord.srmechanicadmin?.email || ""}
                  </div>
                </div>
              </div>

              <p className="mnt-section-title">Request Information</p>
              <div className="mnt-info-grid">
                <div className="mnt-info-item"><label>Full Name</label><span>{selectedRecord.srrentaladmin?.fullname}</span></div>
                <div className="mnt-info-item"><label>Machinery Type</label><span>{selectedRecord.machinerytype}</span></div>
                <div className="mnt-info-item"><label>Preferred Date</label><span>{selectedRecord.preferreddate}</span></div>
                <div className="mnt-info-item"><label>Location</label><span>{selectedRecord.location}</span></div>
                <div className="mnt-info-item"><label>Booking Fee</label><span>Rs. {selectedRecord.bookingfee}</span></div>
                <div className="mnt-info-item"><label>Payment</label><span>{selectedRecord.payment || "Pending"}</span></div>
              </div>
              <p className="mnt-section-title">Issue Description</p>
              <div className="mnt-notes-box">{selectedRecord.descriptionissue}</div>
            </div>
            <div className="mnt-modal-footer">
              <button className="mnt-mf-btn mnt-mf-dispute"
                onClick={() => { setShowViewModal(false); handleDispute(selectedRecord); }}>
                <FiAlertCircle size={14} /> Raise Dispute
              </button>
              {selectedRecord.status === 'completed' && (
                <button className="mnt-mf-btn mnt-mf-update"
                  onClick={() => { setShowViewModal(false); handleReview(selectedRecord); }}>
                  <FiStar size={14} /> Write a Review
                </button>
              )}
              <button className="mnt-mf-btn mnt-mf-close" onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ════ REVIEW MODAL ════ */}
      {showReviewModal && selectedRecord && (
        <div className="mnt-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="mnt-modal mnt-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="mnt-modal-header">
              <div className="mnt-modal-header-left">
                <h5>Write a Review</h5>
                <span>{selectedRecord?.machinerytype} — {selectedRecord?.fullname}</span>
              </div>
              <button className="mnt-modal-close" onClick={() => setShowReviewModal(false)}><FiX /></button>
            </div>
            <div className="mnt-modal-body">
              <Form onSubmit={handleReviewSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className="mnt-form-label">Rating</Form.Label>
                  <div className="mnt-star-row">
                    {[1, 2, 3, 4, 5].map(star => (
                      <span key={star} onClick={() => setRating(star)}
                        className={`mnt-star ${star <= rating ? 'mnt-star-filled' : 'mnt-star-empty'}`}>★</span>
                    ))}
                  </div>
                  {rating === 0 && <small className="mnt-rating-error">Please select a rating</small>}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="mnt-form-label">Your Review</Form.Label>
                  <Form.Control as="textarea" rows={4} className="mnt-form-textarea"
                    placeholder="Share your experience with this mechanic..."
                    value={reviewText} onChange={e => setReviewText(e.target.value)} required />
                </Form.Group>
                <button type="submit" disabled={rating === 0}
                  className={`mnt-save-btn w-100 ${rating === 0 ? 'mnt-btn-disabled' : ''}`}>
                  Submit Review
                </button>
              </Form>
            </div>
          </div>
        </div>
      )}

      {/* ════ DISPUTE MODAL ════ */}
      {showDisputeModal && selectedRecord && (
        <div className="mnt-overlay" onClick={() => setShowDisputeModal(false)}>
          <div className="mnt-modal mnt-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="mnt-modal-header">
              <div className="mnt-modal-header-left">
                <h5>Raise a Dispute</h5>
                <span>Request #{selectedRecord.id} — {selectedRecord.fullname}</span>
              </div>
              <button className="mnt-modal-close" onClick={() => setShowDisputeModal(false)}><FiX /></button>
            </div>
            <div className="mnt-modal-body">

              {/* ✅ Raised By — Rental Admin's own name/email/image */}
              <p className="mnt-section-title">Raised By</p>
              <div className="mnt-mechanic-strip" style={{ marginBottom: '18px' }}>
                <img
                  src={`${baseUrlForFile}/${loggedInRental?.image}`}
                  alt={loggedInRental?.fullname}
                  className="mnt-mechanic-avatar-img"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="mnt-mechanic-info">
                  <div className="mnt-mechanic-name">
                    {loggedInRental?.businessname || loggedInRental?.fullname || `Rental #${loggedInRental?.id}`}
                  </div>
                  <div className="mnt-mechanic-email">
                    {loggedInRental?.email || ""}
                  </div>
                </div>
              </div>

              {/* ✅ Against — Mechanic's name/email/image */}
              <p className="mnt-section-title">Against</p>
              <div className="mnt-mechanic-strip">
                <img
                  src={`${baseUrlForFile}/${selectedRecord.srmechanicadmin?.image}`}
                  alt={selectedRecord.srmechanicadmin?.fullname}
                  className="mnt-mechanic-avatar-img"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="mnt-mechanic-info">
                  <div className="mnt-mechanic-name">
                    {selectedRecord.srmechanicadmin?.fullname || selectedRecord.fullname || `Request #${selectedRecord.id}`}
                  </div>
                  <div className="mnt-mechanic-email">
                    {selectedRecord.srmechanicadmin?.email || ""}
                  </div>
                </div>
              </div>

              <div className="mnt-info-grid" style={{ marginTop: '18px' }}>
                <div className="mnt-info-item">
                  <label>Machinery</label>
                  <span>{selectedRecord.machinerytype}</span>
                </div>
                <div className="mnt-info-item">
                  <label>Preferred Date</label>
                  <span>{selectedRecord.preferreddate}</span>
                </div>
              </div>
              <p className="mnt-section-title">Create Dispute Ticket</p>
              <Form onSubmit={riseDispute}>
                <Form.Group className="mb-3">
                  <Form.Label className="mnt-form-label">Category *</Form.Label>
                  <Form.Select className="mnt-form-select" value={category} onChange={e => setCategory(e.target.value)} required>
                    <option value="">Select Category</option>
                    <option value="Repair Quality">Repair Quality</option>
                    <option value="Service Delay">Service Delay</option>
                    <option value="Equipment Damage">Equipment Damage</option>
                    <option value="Overcharging">Overcharging</option>
                    <option value="Unprofessional Behavior">Unprofessional Behavior</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="mnt-form-label">Description *</Form.Label>
                  <Form.Control as="textarea" rows={4} className="mnt-form-textarea"
                    placeholder="Describe the issue with this mechanic service..."
                    value={description} onChange={e => setDescription(e.target.value)} required />
                </Form.Group>
                <button type="submit" className="mnt-save-btn w-100">Submit Dispute Ticket</button>
              </Form>
            </div>
          </div>
        </div>
      )}

    </RentalAdminLayout>
  );
};

export default Maintenance;